import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import {
  ICommunityData,
  ICommunityTranslation,
} from '@/types/community-management.types';
import { useSnackbar } from 'notistack';
import { useTranslateCommunityContentMutation } from '@/services/CommunityManagementService';

interface CommunityPreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<ICommunityData>;
  source_language?: string;
  initialTranslations?: Record<string, ICommunityTranslation> | null;
  onTranslationsGenerated?: (
    translations: Record<string, ICommunityTranslation>
  ) => void;
}

const hasTranslations = (
  value?: Record<string, ICommunityTranslation> | null
): value is Record<string, ICommunityTranslation> =>
  Boolean(value && Object.keys(value).length > 0);

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : undefined;
  }

  const maybeError = error as {
    data?: unknown;
    message?: unknown;
    error?: unknown;
  };

  if (maybeError.data) {
    if (typeof maybeError.data === 'string') return maybeError.data;

    if (typeof maybeError.data === 'object') {
      const data = maybeError.data as {
        message?: unknown;
        error?: unknown;
      };

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  if (typeof maybeError.message === 'string') return maybeError.message;
  if (typeof maybeError.error === 'string') return maybeError.error;

  return undefined;
};

interface LanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  formData: Partial<ICommunityData>;
  translatedContent?: Record<string, ICommunityTranslation>;
  isEditable?: boolean;
  isSource?: boolean;
  onEditContent?: (
    languageCode: string,
    field: 'description' | 'tags',
    value: string | string[]
  ) => void;
}

const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  formData,
  translatedContent,
  isEditable = true,
  isSource = false,
  onEditContent,
}) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);

  const lowerCode = (language.code || '').toLowerCase();
  const upperCode = (language.code || '').toUpperCase();
  const translatedForCode = useMemo(
    () =>
      translatedContent?.[lowerCode] || translatedContent?.[upperCode] || {},
    [translatedContent, lowerCode, upperCode]
  );
  const displayName = formData.name || '-';
  const displayDescription = isSource
    ? formData.description || '-'
    : ((translatedForCode as ICommunityTranslation)?.description ??
      formData.description ??
      '-');
  const displayTags = isSource
    ? Array.isArray(formData.tags)
      ? (formData.tags as string[])
      : []
    : Array.isArray((translatedForCode as ICommunityTranslation)?.tags)
      ? ((translatedForCode as ICommunityTranslation)?.tags as string[])
      : Array.isArray(formData.tags)
        ? (formData.tags as string[])
        : [];

  useEffect(() => {
    setDescription(
      (translatedForCode as ICommunityTranslation)?.description ?? ''
    );
    setTags(
      Array.isArray((translatedForCode as ICommunityTranslation)?.tags)
        ? [...((translatedForCode as ICommunityTranslation)?.tags as string[])]
        : []
    );
  }, [translatedForCode]);

  const toggleEdit = () => {
    if (!isEditable) return;
    setIsEditing((prev) => !prev);
  };

  const handleTagChange = (index: number, value: string) => {
    setTags((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    const nextTags = [...tags];
    nextTags[index] = value;
    if (onEditContent) {
      onEditContent(language.code, 'tags', nextTags);
    }
  };

  const handleAddTag = () => {
    setTags((prev) => [...prev, '']);
    if (onEditContent) {
      onEditContent(language.code, 'tags', [...tags, '']);
    }
  };

  const handleRemoveTag = (index: number) => {
    const nextTags = tags.filter((_, i) => i !== index);
    setTags(nextTags);
    if (onEditContent) {
      onEditContent(language.code, 'tags', nextTags);
    }
  };

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-200 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h2 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
          </h2>
        </div>

        {isEditable && (
          <button
            type="button"
            onClick={toggleEdit}
            className={clsx(
              'text-blue-600 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 transition-colors',
              isEditing && 'bg-blue-100'
            )}
            title={
              isEditing
                ? intl.formatMessage({ id: 'COMMON.CANCEL' })
                : intl.formatMessage({ id: 'COMMON.EDIT' })
            }
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="p-5">
        {isEditing ? (
          <div className="space-y-6">
            <div>
              <span className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block">
                {intl.formatMessage({ id: 'COMMUNITY.FORM.NAME' })}
              </span>
              <p className="text-base font-medium text-gray-800">
                {displayName}
              </p>
            </div>

            <div>
              <label
                htmlFor={`description-${language.code}`}
                className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block"
              >
                {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
              </label>
              <textarea
                id={`description-${language.code}`}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (onEditContent) {
                    onEditContent(language.code, 'description', e.target.value);
                  }
                }}
                className="w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[80px]"
                autoComplete="off"
              />
            </div>

            <div>
              {tags.length > 0 ? (
                <label
                  htmlFor={`tag-${language.code}-0`}
                  className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block"
                >
                  {intl.formatMessage({ id: 'COMMUNITY.FORM.TAGS' })}
                </label>
              ) : (
                <span className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block">
                  {intl.formatMessage({ id: 'COMMUNITY.FORM.TAGS' })}
                </span>
              )}
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <input
                      id={`tag-${language.code}-${index}`}
                      type="text"
                      value={tag}
                      onChange={(e) => handleTagChange(index, e.target.value)}
                      className="p-2 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      autoComplete="off"
                      aria-label={`${intl.formatMessage({ id: 'COMMUNITY.FORM.TAGS' })} ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(index)}
                      className="text-red-500 hover:text-red-600"
                      aria-label={intl.formatMessage({ id: 'COMMON.REMOVE' })}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="text-blue-600 contrast:text-blue-200 text-xs font-medium hover:text-blue-800 contrast:hover:text-blue-400"
                >
                  + {intl.formatMessage({ id: 'COMMUNITY.FORM.TAGS.ADD' })}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <span className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block">
                {intl.formatMessage({ id: 'COMMUNITY.FORM.NAME' })}
              </span>
              <p className="text-base font-medium text-gray-800">
                {displayName}
              </p>
            </div>

            <div>
              <span className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block">
                {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
              </span>
              <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
                {displayDescription}
              </p>
            </div>

            {displayTags && displayTags.length > 0 && (
              <div>
                <span className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1 block">
                  {intl.formatMessage({ id: 'COMMUNITY.FORM.TAGS' })}
                </span>
                <div className="flex flex-wrap gap-2">
                  {displayTags.map((tag: string) => (
                    <span
                      key={tag}
                      className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-xs font-medium px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CommunityPreviewModal: React.FC<CommunityPreviewModalProps> = ({
  formData,
  source_language,
  open,
  onClose,
  className,
  initialTranslations,
  onTranslationsGenerated,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [translateCommunityContent] = useTranslateCommunityContentMutation();
  const [translations, setTranslations] = useState<
    Record<string, ICommunityTranslation>
  >(() => initialTranslations ?? formData.translations ?? {});
  const [isLoading, setIsLoading] = useState(false);
  const [contentModified, setContentModified] = useState(false);
  const [lastFormData, setLastFormData] = useState<string>('');

  const handleClose = () => {
    onTranslationsGenerated?.(translations);
    onClose?.({}, 'backdropClick');
  };

  useEffect(() => {
    if (!open) return;

    if (hasTranslations(initialTranslations)) {
      setTranslations(initialTranslations);
      return;
    }

    if (hasTranslations(formData.translations)) {
      setTranslations(formData.translations);
      return;
    }

    setTranslations({});
  }, [open, formData.id, formData.translations, initialTranslations]);

  useEffect(() => {
    if (open) {
      setContentModified(false);
    }
  }, [open]);

  useEffect(() => {
    const currentFormDataString = JSON.stringify({
      description: formData.description,
      tags: formData.tags,
    });

    if (lastFormData && lastFormData !== currentFormDataString) {
      setContentModified(true);
      setLastFormData(currentFormDataString);
    } else if (!lastFormData) {
      setLastFormData(currentFormDataString);
      setContentModified(false);
    }
  }, [formData, lastFormData]);

  const fetchTranslations = useCallback(async (forceRegenerate = false) => {
    if (hasTranslations(initialTranslations) && !forceRegenerate) {
      setTranslations(initialTranslations);
      onTranslationsGenerated?.(initialTranslations);
      return;
    }

    if (hasTranslations(formData.translations) && !forceRegenerate) {
      setTranslations(formData.translations);
      onTranslationsGenerated?.(formData.translations);
      return;
    }

    if (hasTranslations(translations) && !contentModified && !forceRegenerate) {
      onTranslationsGenerated?.(translations);
      return;
    }

    if (!formData.description && !(formData.tags && formData.tags.length > 0)) {
      enqueueSnackbar('Please provide description or tags to translate', {
        variant: 'warning',
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        description: formData.description,
        tags: formData.tags,
        source_language: source_language || formData.language || 'en',
        community_id:
          formData.id && !contentModified ? String(formData.id) : undefined,
      };

      const response = await translateCommunityContent(payload).unwrap();
      const warningMessage = response?.warning_message;

      const message = response?.message || 'Translations generated successfully';

      if (response.translations) {
        const raw = response.translations as Record<
          string,
          ICommunityTranslation
        >;
        const normalized: Record<string, ICommunityTranslation> =
          Object.fromEntries(
            Object.entries(raw).map(([k, v]) => [k.toLowerCase(), v || {}])
          );
        setTranslations(normalized);
        setContentModified(false);
        onTranslationsGenerated?.(normalized);

        if (typeof warningMessage === 'string' && warningMessage.trim()) {
          enqueueSnackbar(warningMessage, { variant: 'warning' });
        } else {
          enqueueSnackbar(message, { variant: 'success' });
        }
      }
    } catch (error) {
      console.error('Community translation failed:', error);
      enqueueSnackbar(
        getApiErrorMessage(error) ||
        intl.formatMessage({ id: 'POI.PREVIEW.TRANSLATION_ERROR' }),
        {
          variant: 'warning',
        }
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    initialTranslations,
    formData.description,
    formData.translations,
    formData.tags,
    formData.id,
    source_language,
    contentModified,
    translations,
    translateCommunityContent,
    onTranslationsGenerated,
    enqueueSnackbar,
    intl,
  ]);

  useEffect(() => {
    if (!open) return;
    void fetchTranslations();
  }, [open, fetchTranslations]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="community-preview-modal-title"
      aria-describedby="community-preview-modal-description"
    >
      <div className="harbor-preview-modal bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-y-auto max-h-[560px] custom-scrollbar">
        <div className="modal-header bg-gray-100 px-6 py-4 border-b border-gray-100">
          <div className="modal-header-row flex justify-between items-center w-full">
            <h1
              id="community-preview-modal-title"
              className="text-xl font-bold text-gray-800 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {intl.formatMessage({ id: 'COMMUNITY.PREVIEW.TITLE' })}
            </h1>
            <button
              type="button"
              onClick={handleClose}
              className="modal-close text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={intl.formatMessage({ id: 'COMMUNITY.BUTTON.CLOSE' })}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <p
            id="community-preview-modal-description"
            className="text-sm text-gray-500 mb-4 flex items-center"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {intl.formatMessage({ id: 'COMMUNITY.PREVIEW.DESCRIPTION' })}
          </p>

          <div className="lang-list pr-2 custom-scrollbar">
            {I18N_LANGUAGES.map((lang) => {
              const isSource =
                lang.code === (source_language || formData.language || 'en');

              return (
                <LanguagePreview
                  key={lang.code}
                  language={lang}
                  formData={formData}
                  translatedContent={translations}
                  isEditable={!isSource}
                  isSource={isSource}
                  onEditContent={(languageCode, field, value) => {
                    setTranslations((prev) => {
                      const next = { ...prev };
                      const current = next[languageCode] || {};
                      next[languageCode] = {
                        ...current,
                        [field]: value,
                      };

                      onTranslationsGenerated?.(next);

                      return next;
                    });
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-100 border-t border-gray-100">
          <div className="footer-actions-row flex justify-between items-center">
            <button
              type="button"
              onClick={() => void fetchTranslations(true)}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-900 contrast:bg-green-900 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Regenerate translations"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {intl.formatMessage({ id: 'COMMON.GENERATING' })}
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {intl.formatMessage({ id: 'COMMON.REGENERATE' })}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors duration-200"
              aria-label={intl.formatMessage({ id: 'COMMUNITY.BUTTON.CLOSE' })}
            >
              {intl.formatMessage({ id: 'COMMUNITY.BUTTON.CLOSE' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CommunityPreviewModal;
